import { useQueryClient } from "@tanstack/react-query";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { CellFullscreenDialog } from "./TableComponents/RowCell/Cells/CellFullscreenDialog";

import { useAuth } from "@/context/auth-context";
import { GetReportResponse, useDatasetInfo } from "@/queries";
import { ENDPOINTS } from "@/api/application-api";
import { usePagination } from "@/hooks/UsePagination";
import { useGetDatasetGroups } from "@/queries/datasets";
import { DatasetGroup } from "@/queries/datasets/types";
import { DatasetRead } from "@/types/datasets";
import { Report, ReportCell, ReportColumn } from "@/types/evaluate";
import { sortColumns } from "@/utils/evaluate";

export interface SortConfigItem {
  columnId: number;
  direction: "asc" | "desc" | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ReportContextType extends Record<string, any> {
  reportMetadata: GetReportResponse;
  sortConfig: SortConfigItem[];
  reportCells: ReportCell[] | null;
  setReportCells: React.Dispatch<React.SetStateAction<ReportCell[] | null>>;
  visibleRows: number[] | null;
  setVisibleRows: (rows: number[]) => void;
  selectedColumn: ReportColumn | null;
  setSelectedColumn: (column: ReportColumn | null) => void;
  persistedDraftColumn: ReportColumn | null;
  setPersistedDraftColumn: (column: ReportColumn | null) => void;
  clearPersistedDraftColumn: () => void;
  hasPersistedDraftColumn: boolean;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: (skipDraftPersistence?: boolean) => void;
  fullscreenDialogState: {
    isOpen: boolean;
    cell: ReportCell | null;
    column: ReportColumn | null;
    viewMode?: "normal" | "raw";
  };
  openFullscreenDialog: (
    cell: ReportCell,
    column: ReportColumn,
    viewMode?: "normal" | "raw",
  ) => void;
  closeFullscreenDialog: () => void;
  currentDatasetGroup: DatasetGroup | undefined;
}

export const ReportContext = createContext<ReportContextType | null>(null);

interface ReportProviderProps extends PropsWithChildren {
  reportMetadata: GetReportResponse;
  editable: boolean;
}

function ReportProvider({ reportMetadata, ...props }: ReportProviderProps) {
  const userToken = useAuth()?.userToken || "";
  const reportId = reportMetadata.report.id;
  const queryClient = useQueryClient();
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reportColumns, setReportColumns] = useState<ReportColumn[]>([]);
  const [reportCells, setReportCells] = useState<ReportCell[] | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | undefined>();
  const [visibleRows, setVisibleRows] = useState<number[] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortedRowIds, setSortedRowIds] = useState<number[] | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<ReportColumn | null>(null);
  const [persistedDraftColumn, setPersistedDraftColumn] = useState<ReportColumn | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [cellStatus, setCellStatus] = useState<string[] | null>(null);
  const [fullscreenDialogState, setFullscreenDialogState] = useState<{
    isOpen: boolean;
    cell: ReportCell | null;
    column: ReportColumn | null;
    viewMode?: "normal" | "raw";
  }>({
    isOpen: false,
    cell: null,
    column: null,
    viewMode: "normal",
  });

  const setVisibleRowsAndClearUpdated = useCallback(
    (newVisibleRows: number[]) => {
      if (!visibleRows || visibleRows.join(",") !== newVisibleRows.join(",")) {
        setLastUpdated(undefined);
        setVisibleRows(newVisibleRows);
      }
    },
    [visibleRows],
  );

  const [lastCellNumber, setLastCellNumber] = useState<number | null>(null);
  useEffect(() => {
    if (reportMetadata) {
      const newCount = Object.values(reportMetadata.stats.status_counts).reduce(
        (total, value) => total + value,
        0,
      );
      if (lastCellNumber !== newCount) {
        setLastCellNumber(newCount);
        setLastUpdated(undefined);
        queryClient.invalidateQueries([ENDPOINTS.report_row_ids(reportId)]);
      }
    }
  }, [reportMetadata, lastCellNumber, queryClient, reportId]);

  const paginationRange = usePagination({
    totalCount: (sortedRowIds || []).length,
    pageSize,
  });

  useEffect(() => {
    if (!sortedRowIds) {
      return;
    }
    const newVisibleRows = sortedRowIds.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    if (!visibleRows || visibleRows.join(",") !== newVisibleRows.join(",")) {
      setVisibleRows(newVisibleRows);
    }
  }, [visibleRows, setVisibleRows, sortedRowIds, currentPage, pageSize, paginationRange.length]);

  // Reset current page when page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  // getters

  const { data: datasetInfoResponse } = useDatasetInfo(userToken, reportMetadata.report.dataset_id);

  const datasetInfo = {
    ...datasetInfoResponse,
  };

  // Create optimized cell lookup map for O(1) access
  const cellMap = useMemo(() => {
    if (!reportCells) {
      return new Map<string, ReportCell>();
    }

    const map = new Map<string, ReportCell>();
    reportCells.forEach((cell: ReportCell) => {
      // Create keys for both possible row ID types
      const datasetKey = `${cell.dataset_row_id}-${cell.report_column_id}`;
      const idKey = `${cell.id}-${cell.report_column_id}`;
      map.set(datasetKey, cell);
      map.set(idKey, cell);
    });
    return map;
  }, [reportCells]);

  const getCellByRowAndColumn = useCallback(
    (rowId: number, columnId: number) => {
      const key = `${rowId}-${columnId}`;
      return cellMap.get(key);
    },
    [cellMap],
  );

  // These table helpers are calculated in the context so that
  // the table doesn't have to re-render when cells are updated

  // Building columns
  const sortedColumns = useMemo(() => sortColumns(reportColumns || []), [reportColumns]);
  const lastColumnId = useMemo(() => {
    const lastColumn = sortedColumns[sortedColumns.length - 1];
    return lastColumn?.id || -1;
  }, [sortedColumns]);

  // Check if we have a draft column (no ID) that needs a placeholder
  const draftColumn = useMemo(() => {
    if (selectedColumn && !selectedColumn.id) {
      return {
        ...selectedColumn,
        id: -999, // Temporary ID for placeholder
        name: selectedColumn.name || "New Step",
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedColumn?.id,
    selectedColumn?.name,
    selectedColumn?.column_type,
    selectedColumn?.configuration,
  ]);

  // Include placeholder for draft columns
  const columnsWithPlaceholder = useMemo(() => {
    const columns = [...sortedColumns];

    // Add placeholder for draft column if one exists
    if (draftColumn) {
      columns.push(draftColumn);
    }

    return columns;
  }, [sortedColumns, draftColumn]);

  const tableColumnsMap = useMemo(() => {
    const columnCellsMap: Record<number, ReportColumn> = {};
    columnsWithPlaceholder.forEach((column: ReportColumn) => {
      const columnId: number = column.id || -1;
      columnCellsMap[columnId] = column;
    });
    return columnCellsMap;
  }, [columnsWithPlaceholder]);
  const getColumnById = useCallback(
    (columnId: number) => tableColumnsMap[columnId],
    [tableColumnsMap],
  );

  // Sort only - search/filter moved to MobX store
  const [sortConfig, setSortConfig] = useState<SortConfigItem[]>([]);

  const sortColumn = useCallback((columnId: number, direction: "asc" | "desc" | null) => {
    setSortConfig(prevConfig => {
      const columnIndex = prevConfig.findIndex(item => item.columnId === columnId);

      if (columnIndex === -1) {
        return [...prevConfig, { columnId, direction }];
      }
      const newConfig = prevConfig.filter(item => item.columnId !== columnId);
      if (direction !== null) {
        newConfig.push({ columnId, direction });
      }
      return newConfig;
    });
  }, []);

  const { data: datasetGroups } = useGetDatasetGroups({
    dataSetId: reportMetadata.report.dataset_id.toString(),
    minVersionNumber: 0,
  });

  const currentDatasetGroup = datasetGroups?.dataset_groups?.find((datasetGroup: DatasetGroup) =>
    datasetGroup.datasets?.find(
      (dataset: DatasetRead) => dataset.id === reportMetadata.report.dataset_id,
    ),
  );

  const latestDataset = currentDatasetGroup?.datasets?.[currentDatasetGroup.datasets.length - 1];

  const clearPersistedDraftColumn = useCallback(() => {
    setPersistedDraftColumn(null);
  }, []);

  const hasPersistedDraftColumn = useMemo(
    () => persistedDraftColumn !== null,
    [persistedDraftColumn],
  );

  const openSidebar = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(
    (skipDraftPersistence = false) => {
      // Persist draft column if it's being edited (unless explicitly skipped)
      if (selectedColumn && !selectedColumn.id && !skipDraftPersistence) {
        setPersistedDraftColumn(selectedColumn);
      }
      setIsSidebarOpen(false);
      setSelectedColumn(null);
    },
    [selectedColumn],
  );

  const openFullscreenDialog = useCallback(
    (cell: ReportCell, column: ReportColumn, viewMode: "normal" | "raw" = "normal") => {
      setFullscreenDialogState({
        isOpen: true,
        cell,
        column,
        viewMode,
      });
    },
    [],
  );

  const closeFullscreenDialog = useCallback(() => {
    setFullscreenDialogState({
      isOpen: false,
      cell: null,
      column: null,
      viewMode: "normal",
    });
  }, []);

  return (
    <ReportContext.Provider
      value={{
        columnsCount: columnsWithPlaceholder.length,
        datasetInfo,
        latestDataset,
        getCellByRowAndColumn,
        getColumnById,
        isEditable: props.editable,
        lastColumnId,
        reportCells,
        reportColumns,
        setReportColumns,
        setReportCells,
        selectedReport,
        setSelectedReport,
        sortConfig,
        sortColumn,
        reportId,
        reportMetadata,
        sortedColumns: columnsWithPlaceholder,
        lastUpdated,
        setLastUpdated,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        paginationRange,
        sortedRowIds,
        setSortedRowIds,
        visibleRows,
        setVisibleRows: setVisibleRowsAndClearUpdated,
        selectedColumn,
        setSelectedColumn,
        isSidebarOpen,
        openSidebar,
        closeSidebar,
        cellStatus,
        setCellStatus,
        fullscreenDialogState,
        openFullscreenDialog,
        closeFullscreenDialog,
        persistedDraftColumn,
        setPersistedDraftColumn,
        clearPersistedDraftColumn,
        hasPersistedDraftColumn,
        currentDatasetGroup,
      }}
      {...props}
    >
      {props.children}
      {fullscreenDialogState.isOpen && (
        <CellFullscreenDialog
          isOpen={fullscreenDialogState.isOpen}
          onClose={closeFullscreenDialog}
          cell={fullscreenDialogState.cell}
          column={fullscreenDialogState.column}
          viewMode={fullscreenDialogState.viewMode}
        />
      )}
    </ReportContext.Provider>
  );
}

const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error("useReportContext must be used within a ReportProvider");
  }
  return context;
};

export { ReportProvider, useReportContext };
